import React, { useEffect, useRef } from 'react';
import { useGoogleMapsContext } from '../providers/GoogleMapsProvider';
import './AddressAutocomplete.css';

/**
 * Google Places Autocomplete input component.
 * Provides address suggestions as user types and emits full place data on selection.
 *
 * Part of Store Location Editor feature (STORE_LOCATION_EDITOR_ENTERPRISE_PLAN).
 *
 * @param {Object} props
 * @param {string} props.value - Current input value
 * @param {function} props.onChange - Called when input value changes
 * @param {function} props.onPlaceSelected - Called when user selects a place from suggestions
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.locationBias - Optional location bias {lat, lng, radius} to prioritize nearby results
 *
 * @example
 * <AddressAutocomplete
 *   value={address}
 *   onChange={(e) => setAddress(e.target.value)}
 *   onPlaceSelected={(place) => {
 *     console.log('Selected:', place.address, place.latitude, place.longitude);
 *   }}
 *   locationBias={{ lat: 17.421268, lng: 78.588932, radius: 10000 }}
 *   placeholder="Search for your store address..."
 * />
 */
const AddressAutocomplete = ({
  value = '',
  onChange,
  onPlaceSelected,
  disabled = false,
  placeholder = 'Search for an address...',
  className = '',
  locationBias = null
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const { isLoaded, loadError } = useGoogleMapsContext();
  const locationBiasLat = locationBias?.lat ?? null;
  const locationBiasLng = locationBias?.lng ?? null;
  const locationBiasRadius = locationBias?.radius ?? 10000;

  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    // Wait for Google Maps to load
    if (!isLoaded || !window.google?.maps?.places || !inputRef.current) {
      return;
    }

    // Prevent duplicate initialization
    if (autocompleteRef.current) {
      return;
    }

    try {
      // Build autocomplete options
      const autocompleteOptions = {
        types: ['address'], // More inclusive than 'establishment' - includes all addresses
        componentRestrictions: { country: 'in' }, // Restrict to India
        fields: [
          'address_components',
          'formatted_address',
          'geometry',
          'place_id',
          'name'
        ]
      };

      // Add location bias if provided (prioritizes results near this location)
      if (locationBiasLat && locationBiasLng) {
        const center = new window.google.maps.LatLng(locationBiasLat, locationBiasLng);

        autocompleteOptions.locationBias = new window.google.maps.Circle({
          center: center,
          radius: locationBiasRadius
        }).getBounds();
      }

      // Initialize Google Places Autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        autocompleteOptions
      );

      autocompleteRef.current = autocomplete;

      // Listen for place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();

        // Ensure place has geometry (valid selection)
        if (!place.geometry || !place.geometry.location) {
          console.warn('[AddressAutocomplete] No geometry for selected place:', place);
          return;
        }

        // Extract address components
        const addressComponents = place.address_components || [];
        const extractComponent = (types) => {
          const component = addressComponents.find(c =>
            types.some(type => c.types.includes(type))
          );
          return component ? component.long_name : null;
        };

        // Build place data object
        const placeData = {
          address: place.formatted_address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          placeId: place.place_id,
          name: place.name || null,
          addressComponents: {
            pincode: extractComponent(['postal_code']),
            city: extractComponent(['locality', 'sublocality', 'administrative_area_level_2']),
            state: extractComponent(['administrative_area_level_1']),
            country: extractComponent(['country'])
          }
        };

        // Emit to parent
        onPlaceSelectedRef.current?.(placeData);
      });
    } catch (error) {
      // Silently handle initialization errors (logged in GoogleMapsProvider)
    }

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded, locationBiasLat, locationBiasLng, locationBiasRadius]);

  // Show error state
  if (loadError) {
    return (
      <div className="address-autocomplete-error">
        <input
          type="text"
          disabled
          placeholder="Google Maps failed to load"
          className={`address-autocomplete-input error ${className}`}
        />
        <p className="error-message">{loadError}</p>
      </div>
    );
  }

  // Show loading state
  if (!isLoaded) {
    return (
      <input
        type="text"
        disabled
        placeholder="Loading Google Maps..."
        className={`address-autocomplete-input loading ${className}`}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className={`address-autocomplete-input ${className}`}
      autoComplete="off"
    />
  );
};

export default AddressAutocomplete;
